import { Autocomplete, AutocompleteProps, Paper, useTheme } from '@mui/material'
import { useCallback } from 'react'
import { MdChevronLeft } from 'react-icons/md'
import { AppTextField } from './AppTextField'

type MaybeBool = boolean | undefined

type CustomAutocompleteProps<T, FreeSolo extends boolean | undefined = false> = Omit<
  AutocompleteProps<T, MaybeBool, MaybeBool, FreeSolo>,
  'options' | 'renderInput'
>

export type SelectFieldProps<T = any, FreeSolo extends boolean | undefined = false> = CustomAutocompleteProps<
  T,
  FreeSolo
> & {
  name?: string
  data: T[]
  label?: string
  error?: boolean
  helperText?: string
  variant?: 'standard' | 'outlined' | 'filled'
  listboxMinWidth?: number | string
  enableSloopySearch?: boolean
}

export function SelectField<T = any, FreeSolo extends boolean | undefined = false>({
  data,
  label,
  error,
  helperText,
  variant,
  multiple,
  value,
  listboxMinWidth,
  filterOptions,
  enableSloopySearch = false,
  ...rest
}: SelectFieldProps<T, FreeSolo>) {
  const theme = useTheme()
  const normalizedValue = multiple && value !== undefined ? (Array.isArray(value) ? value : []) : value

  const filterByWords = (options: T[], state: { inputValue: string; getOptionLabel: (option: T) => string }) => {
    const words = state.inputValue.toLowerCase().trim().split(/\s+/).filter(Boolean)

    if (words.length === 0) return options

    return options.filter(option => {
      const labelText = state.getOptionLabel(option).toLowerCase()
      return words.every(word => labelText.includes(word))
    })
  }

  const CustomPaper = useCallback(
    props => <Paper elevation={16} {...props} sx={{ ...(listboxMinWidth && { minWidth: listboxMinWidth }) }} />,
    [listboxMinWidth],
  )

  return (
    <Autocomplete
      popupIcon={<MdChevronLeft transform="rotate(-90)" />}
      options={data ?? []}
      PaperComponent={CustomPaper}
      multiple={multiple}
      value={normalizedValue}
      filterOptions={enableSloopySearch ? filterByWords : filterOptions}
      ListboxProps={{
        style: {
          maxHeight: 200,
          overflow: 'auto',
          backgroundColor: theme.palette.white.main,
          boxShadow: `0px 2px 4px ${theme.palette.darkTransparentBlack.main}`,
          fontSize: '14px',
          fontWeight: '400',
          ...(listboxMinWidth && { minWidth: listboxMinWidth }),
        },
      }}
      {...rest}
      renderInput={params => (
        <AppTextField variant={variant} {...params} label={label} error={error} helperText={helperText} />
      )}
    />
  )
}
